import { useState } from "react";
import { motion } from "framer-motion";
import { Compass, BookOpen, GraduationCap, Bot, ArrowRight, CheckCircle2 } from "lucide-react";

const STAGES = [
  {
    step: "01",
    venture: "CounselNest",
    stage: "Career Intelligence",
    title: "Scientific Assessments & Career Guidance",
    desc: "Connects students with validated psychometric and DMIT assessments, moving career guidance beyond generic advice through structured professional counselling.",
    icon: Compass,
    accent: "from-[#0B5D4B] to-[#0D9488]",
    pill: "Assess & Clarify",
    bullets: ["Scientific DMIT & aptitude tests", "Certified counsellor network", "Personalised career roadmap"],
    link: "https://counselnest.in",
  },
  {
    step: "02",
    venture: "TutorsNest",
    stage: "Personalised Learning",
    title: "Targeted Tutoring & Subject Mentoring",
    desc: "Connects students with vetted tutors, mentors, and subject-matter experts for personalised academic mastery and competitive exam support.",
    icon: BookOpen,
    accent: "from-[#4F46E5] to-[#6366F1]",
    pill: "Learn & Excel",
    bullets: ["1-on-1 concept-level mastery", "CBSE, ICSE, IB & Entrance exams", "Outcome-driven learning plans"],
    link: "https://tutorsnest.in",
  },
  {
    step: "03",
    venture: "AdmissionsNest",
    stage: "Higher Education",
    title: "Domestic & Global University Admissions",
    desc: "Supports students exploring higher-education opportunities across India and abroad, simplifying university discovery, programs, and admissions.",
    icon: GraduationCap,
    accent: "from-[#B88728] to-[#D4AF37]",
    pill: "Target & Enroll",
    bullets: ["400+ verified campuses", "Application & SOP strategy", "Scholarship discovery & guidance"],
    link: "https://admissionsnest.in",
  },
  {
    step: "04",
    venture: "AskPurple",
    stage: "Technology Support",
    title: "Technology-Led Student Support Layer",
    desc: "Designed to help students get fast answers to academic and education questions and connect them with the right human experts when required.",
    icon: Bot,
    accent: "from-[#6366F1] to-[#818CF8]",
    pill: "Smart AI Layer",
    bullets: ["Instant answers to student questions", "Continuous ecosystem context", "Direct expert escalation"],
    isChatTrigger: true,
  },
];

export default function EcosystemPipeline() {
  const [activeStage, setActiveStage] = useState(0);

  return (
    <section className="relative z-10 px-4 py-12 sm:px-6 md:py-18">
      <div className="mx-auto max-w-7xl">
        
        {/* Section Header */}
        <div className="mb-10 text-center sm:mb-14">
          <div className="mb-3 inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white/90 px-4 py-1 text-xs font-bold uppercase tracking-wider text-slate-700 shadow-sm backdrop-blur">
            <span className="flex h-2 w-2 rounded-full bg-[#0B5D4B] animate-pulse" />
            The Unified Student Journey
          </div>
          <h2 className="text-3xl font-extrabold tracking-tight text-slate-900 sm:text-4xl lg:text-5xl">
            One Connected Ecosystem Across Every Milestone.
          </h2>
          <p className="mx-auto mt-4 max-w-2xl text-sm sm:text-base leading-relaxed text-slate-600">
            Instead of navigating fragmented platforms, Crescet unifies scientific assessments,
            career guidance, admissions, and tutoring into one continuous student journey.
          </p>
        </div>

        {/* 4-Stage Cards Grid */}
        <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
          {STAGES.map((stage, idx) => {
            const Icon = stage.icon;
            const isHovered = activeStage === idx;
            return (
              <motion.div
                key={stage.step}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: idx * 0.1 }}
                onMouseEnter={() => setActiveStage(idx)}
                className={`group relative flex flex-col justify-between rounded-3xl border bg-white p-6 shadow-sm transition-all duration-300 hover:-translate-y-1.5 hover:shadow-xl ${
                  isHovered ? "border-[#0B5D4B]/40 shadow-slate-900/10" : "border-slate-200"
                }`}
              >
                <div>
                  {/* Step Header */}
                  <div className="flex items-center justify-between mb-4">
                    <span className="text-2xl font-black tracking-tight text-slate-200 group-hover:text-slate-300 transition-colors">
                      {stage.step}
                    </span>
                    <span className="rounded-full bg-slate-100 px-2.5 py-0.5 text-[11px] font-bold text-slate-600">
                      {stage.pill}
                    </span>
                  </div>

                  {/* Icon & Stage */}
                  <div className={`mb-4 inline-flex h-12 w-12 items-center justify-center rounded-2xl bg-gradient-to-br ${stage.accent} text-white shadow-md transition-transform duration-300 group-hover:scale-110`}>
                    <Icon size={22} />
                  </div>

                  <p className="text-xs font-bold uppercase tracking-wider text-[#0B5D4B]">
                    {stage.stage}
                  </p>
                  <h3 className="mt-1 text-lg font-bold text-slate-900 leading-snug">
                    {stage.title}
                  </h3>
                  <p className="mt-2 text-xs sm:text-sm leading-relaxed text-slate-600">
                    {stage.desc}
                  </p>

                  {/* Micro Bullets */}
                  <div className="mt-4 space-y-1.5 border-t border-slate-100 pt-3">
                    {stage.bullets.map((b) => (
                      <div key={b} className="flex items-center gap-2 text-xs text-slate-700">
                        <CheckCircle2 size={13} className="shrink-0 text-[#0B5D4B]" />
                        <span>{b}</span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Footer Link */}
                <div className="mt-6 pt-3 border-t border-slate-100">
                  {stage.isChatTrigger ? (
                    <button
                      type="button"
                      onClick={() => window.openAskPurple?.()}
                      className="inline-flex items-center text-xs font-bold text-[#0B5D4B] transition-colors group-hover:text-[#064E3B] cursor-pointer"
                    >
                      <span>Open {stage.venture}</span>
                      <ArrowRight size={13} className="ml-1 transition-transform group-hover:translate-x-1" />
                    </button>
                  ) : (
                    <a
                      href={stage.link}
                      target={stage.internal ? "_self" : "_blank"}
                      rel="noopener noreferrer"
                      className="inline-flex items-center text-xs font-bold text-[#0B5D4B] transition-colors group-hover:text-[#064E3B]"
                    >
                      <span>Explore {stage.venture}</span>
                      <ArrowRight size={13} className="ml-1 transition-transform group-hover:translate-x-1" />
                    </a>
                  )}
                </div>
              </motion.div>
            );
          })}
        </div>

      </div>
    </section>
  );
}
